import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import BrandMark, { logoForBrand } from './BrandMark';
import { useLocale, useT } from '../context/LocaleContext';
import { displayName, localeTag } from '../lib/format';
import { runQuickAction } from '../lib/jobActions';
import { JOB_STAGES, QUICK_ACTIONS, STAGE_ICONS, stageIndex } from '../lib/jobStage';

export function Icon({ name, className = '' }) {
  return (
    <span className={`material-symbol ${className}`.trim()} aria-hidden="true">
      {name}
    </span>
  );
}

/** Stage name with its icon; colour is a secondary cue, never the only one. */
export function StageBadge({ stage, size }) {
  const t = useT();
  if (!stage) return null;
  return (
    <span className={`stage-badge stage-${stage}${size === 'lg' ? ' stage-badge-lg' : ''}`}>
      <Icon name={STAGE_ICONS[stage]} />
      {t(`stage.${stage}`)}
    </span>
  );
}

/** Six-segment progress bar for rows and cards. */
export function StageTrack({ stage }) {
  const t = useT();
  const current = stageIndex(stage);
  if (current < 0) return null;
  return (
    <span
      className={`stage-track stage-${stage}`}
      role="img"
      aria-label={`${t(`stage.${stage}`)} · ${current + 1}/${JOB_STAGES.length}`}
    >
      {JOB_STAGES.map((id, index) => (
        <i key={id} className={index < current ? 'done' : index === current ? 'now' : ''} />
      ))}
    </span>
  );
}

const ATTENTION_ICONS = {
  confirm: 'priority_high',
  late: 'schedule',
  unassigned: 'person_off',
  declined: 'thumb_down',
  parts_request: 'inventory_2',
  qc_request: 'fact_check',
  uncollected: 'car_crash',
  chase_customer: 'call',
  stuck: 'timer',
};

export function JobFlags({ job, limit }) {
  const t = useT();
  const flags = [
    ...job.attention.map((id) => ({ id, tone: 'attn', icon: ATTENTION_ICONS[id], label: t(`attn.${id}`) })),
    ...(job.waiting
      ? [{ id: job.waiting, tone: 'wait', icon: 'hourglass_top', label: t(`wait.${job.waiting}`) }]
      : []),
  ];
  if (!flags.length) return null;
  return (
    <span className="job-flags">
      {flags.slice(0, limit || flags.length).map((flag) => (
        <span key={flag.id} className={`job-flag job-flag-${flag.tone}`}>
          <Icon name={flag.icon} />
          {flag.label}
        </span>
      ))}
    </span>
  );
}

/** The job's next step as a short line — the thing a mechanic scans for. */
export function NextStepLabel({ next }) {
  const t = useT();
  if (!next) {
    return <span className="next-label next-none">{t('jobs.done')}</span>;
  }
  return (
    <span className={`next-label${next.kind === 'wait' ? ' next-wait' : ''}`}>
      <Icon name={next.kind === 'wait' ? 'hourglass_top' : 'arrow_forward'} className="rtl-flip" />
      {t(`next.${next.id}`)}
    </span>
  );
}

export function vehicleName(vehicle, t) {
  if (!vehicle) return t('common.vehicle');
  return [vehicle.brand, vehicle.model, vehicle.year]
    .filter((part) => part !== null && part !== undefined && `${part}`.trim() !== '')
    .join(' ');
}

export function Plate({ value }) {
  const t = useT();
  return <span className={`plate-tag${value ? '' : ' plate-empty'}`}>{value || t('bookings.noPlate')}</span>;
}

export function useJobTime() {
  const { locale } = useLocale();
  const tag = localeTag() || (locale === 'ar' ? 'ar-EG' : 'en-GB');
  return {
    time: (date) =>
      date ? new Intl.DateTimeFormat(tag, { hour: '2-digit', minute: '2-digit' }).format(date) : '—',
    day: (date) =>
      date
        ? new Intl.DateTimeFormat(tag, { weekday: 'short', day: 'numeric', month: 'short' }).format(date)
        : '—',
  };
}

/** Time if it is today, otherwise day + time. */
export function JobWhen({ job }) {
  const { time, day } = useJobTime();
  const late = job.attention.includes('late');
  return (
    <span className={`job-when${late ? ' is-late' : ''}`}>
      <strong>{time(job.scheduledAt)}</strong>
      {!job.isToday ? <small>{day(job.scheduledAt)}</small> : null}
    </span>
  );
}

export function formatDuration(hours, t) {
  if (hours == null) return '';
  if (hours < 1) return t('dur.min', { n: Math.max(1, Math.round(hours * 60)) });
  if (hours < 48) return t('dur.h', { n: Math.floor(hours) });
  return t('dur.d', { n: Math.floor(hours / 24) });
}

/** "3 h here" — how long the job has been on its current step. */
export function StageAge({ job }) {
  const t = useT();
  if (job.hoursInStage == null || !['checked_in', 'inspection', 'in_progress', 'ready'].includes(job.stage)) {
    return null;
  }
  return (
    <span className={`stage-age${job.overdue ? ' is-overdue' : ''}`}>
      <Icon name="timer" />
      {t('dur.in', { d: formatDuration(job.hoursInStage, t) })}
    </span>
  );
}

/**
 * The next step, as a one-tap button when it is safe to run from a list;
 * otherwise the plain label. Steps that message the customer need a second tap.
 */
export function QuickAction({ job, onDone }) {
  const t = useT();
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);

  const next = job.next;
  if (!next || !QUICK_ACTIONS.has(next.id)) return <NextStepLabel next={next} />;

  async function onClick(event) {
    event.preventDefault();
    event.stopPropagation();
    if (busy) return;
    if (!armed) {
      setArmed(true);
      setError('');
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setArmed(false), 4000);
      return;
    }
    clearTimeout(timer.current);
    setBusy(true);
    const result = await runQuickAction(job.row, next);
    setBusy(false);
    setArmed(false);
    if (result?.error) {
      setError(t('quick.failed', { msg: result.error.message }));
      return;
    }
    onDone?.();
  }

  return (
    <span className="quick-wrap">
      <button
        type="button"
        className={`quick-btn${armed ? ' is-armed' : ''}`}
        onClick={onClick}
        disabled={busy}
        aria-live="polite"
      >
        <Icon name={armed ? 'touch_app' : next.icon} />
        {busy ? t('common.working') : armed ? t('quick.again') : t(`next.${next.id}`)}
      </button>
      {error ? <small className="quick-error">{error}</small> : null}
    </span>
  );
}

/** Compact card used on the Today board and the Jobs board. */
export function JobCard({ job, makes, serviceLabel, showStage = true, onChanged }) {
  const t = useT();
  const { row } = job;
  return (
    <article className={`job-card-v2 stage-${job.stage}`}>
      <div className="job-card-top">
        <BrandMark name={row.vehicles?.brand} src={logoForBrand(makes, row.vehicles?.brand)} compact />
        <div className="job-card-car">
          <Link to={`/bookings/${row.id}`} className="card-link">
            <strong>{vehicleName(row.vehicles, t)}</strong>
          </Link>
          <Plate value={row.vehicles?.plate_number} />
        </div>
        <JobWhen job={job} />
      </div>
      <div className="job-card-mid">
        <span className="job-card-service">{serviceLabel(row.service_type)}</span>
        <span className="job-card-people">
          <span>
            <Icon name="person" />
            {displayName(row.profiles)}
          </span>
          <span className={job.mechanicName ? '' : 'is-missing'}>
            <Icon name="engineering" />
            {job.mechanicName || t('assign.unassigned')}
          </span>
        </span>
      </div>
      <JobFlags job={job} />
      <div className="job-card-foot">
        <span className="job-card-stage">
          {showStage ? <StageBadge stage={job.stage} /> : <StageTrack stage={job.stage} />}
          <StageAge job={job} />
        </span>
        <QuickAction job={job} onDone={onChanged} />
      </div>
    </article>
  );
}
